import { LocalVideoTrack, Room } from 'twilio-video';
import { useState, useEffect, useCallback } from 'react';
import { SELECTED_BACKGROUND_SETTINGS_KEY, VIDEOBASEURL } from '../../../constants';
import {
  GaussianBlurBackgroundProcessor,
  VirtualBackgroundProcessor,
  ImageFit,
  isSupported,
} from '@twilio/video-processors';
import MEDWhite from '../../../images/MEDWhite.jpg';
import MEDWhiteThumb from '../../../images/thumb/MEDWhite.jpg';
import MEDBlue from '../../../images/MEDBlue.jpg';
import MEDBlueThumb from '../../../images/thumb/MEDBlue.jpg';
import PlainGrey from '../../../images/PlainGrey.jpg';
import PlainGreyThumb from '../../../images/thumb/PlainGrey.jpg';
import { Thumbnail } from '../../BackgroundSelectionDialog/BackgroundThumbnail/BackgroundThumbnail';
import { isMobile } from '../../../utils';

// video-processors 3.x reports support for every browser with a canvas, mobile included. Background effects stay
// desktop-only (as with 1.x, which only supported desktop Chromium): they are CPU-heavy on phones, and this also
// keeps patients' phones from downloading the segmentation model and WASM files on page load.
export const isBackgroundProcessingSupported = () => isSupported && !isMobile;

export interface BackgroundSettings {
  type: Thumbnail;
  index?: number;
}

const imageNames: string[] = ['MED White', 'MED Blue'];

const images = [MEDWhiteThumb, MEDBlueThumb, PlainGreyThumb];

const rawImagePaths = [MEDWhite, MEDBlue, PlainGrey];

let imageElements = new Map();

const getImage = (index: number): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    if (imageElements.has(index)) {
      return resolve(imageElements.get(index));
    }
    const img = new Image();
    img.onload = () => {
      imageElements.set(index, img);
      resolve(img);
    };
    img.onerror = reject;
    img.src = rawImagePaths[index];
  });
};

export const backgroundConfig = {
  imageNames,
  images,
};

const virtualBackgroundAssets = `${VIDEOBASEURL}/virtualbackground`;
// The library's (3.x) defaults, passed explicitly because it logs a warning whenever they are omitted.
const MASK_BLUR_RADIUS = 8;
const BLUR_FILTER_RADIUS = 15;
let blurProcessor: GaussianBlurBackgroundProcessor;
let virtualBackgroundProcessor: VirtualBackgroundProcessor;

const setDefaultBackground = () => {
  const localStorageSettings = window.localStorage.getItem(SELECTED_BACKGROUND_SETTINGS_KEY);
  const searchStr = window.sessionStorage.getItem('searchStr');
  let defaultBackground = { type: 'none', index: 0 };
  let isAgentCall = false;
  if (!!searchStr) {
    const searchParams = new URLSearchParams(searchStr);
    isAgentCall = searchParams.get('isAgentCall') === 'true';
    if (isAgentCall) {
      defaultBackground = { type: 'blur', index: 0 };
    }
  }
  const savedBackground = localStorageSettings ? JSON.parse(localStorageSettings) : null;
  // Agents can only pick None or Blur, so an image background they saved earlier falls back to the default
  if (!savedBackground || (isAgentCall && savedBackground.type === 'image')) {
    return defaultBackground;
  }
  return savedBackground;
};

export default function useBackgroundSettings(videoTrack: LocalVideoTrack | undefined, room?: Room | null) {
  const [backgroundSettings, setBackgroundSettings] = useState<BackgroundSettings>(setDefaultBackground);

  const removeProcessor = useCallback(() => {
    if (videoTrack && videoTrack.processor) {
      videoTrack.removeProcessor(videoTrack.processor);
    }
  }, [videoTrack]);

  const addProcessor = useCallback(
    (processor: GaussianBlurBackgroundProcessor | VirtualBackgroundProcessor) => {
      if (!videoTrack || videoTrack.processor === processor) {
        return;
      }
      removeProcessor();
      // The frame buffer types video-processors 3.x expects (it processes frames in a web worker)
      videoTrack.addProcessor(processor, {
        inputFrameBufferType: 'videoframe',
        outputFrameBufferContextType: 'bitmaprenderer',
      });
    },
    [videoTrack, removeProcessor]
  );

  useEffect(() => {
    if (!isBackgroundProcessingSupported()) {
      return;
    }
    // make sure localParticipant has joined room before applying video processors
    // this ensures that the video processors are not applied on the LocalVideoPreview
    const handleProcessorChange = async () => {
      if (!blurProcessor) {
        blurProcessor = new GaussianBlurBackgroundProcessor({
          assetsPath: virtualBackgroundAssets,
          maskBlurRadius: MASK_BLUR_RADIUS,
          blurFilterRadius: BLUR_FILTER_RADIUS,
        });
        await blurProcessor.loadModel();
      }
      if (!virtualBackgroundProcessor) {
        virtualBackgroundProcessor = new VirtualBackgroundProcessor({
          assetsPath: virtualBackgroundAssets,
          backgroundImage: await getImage(0),
          fitType: ImageFit.Cover,
          maskBlurRadius: MASK_BLUR_RADIUS,
        });
        await virtualBackgroundProcessor.loadModel();
      }
      if (!room?.localParticipant) {
        return;
      }

      if (backgroundSettings.type === 'blur') {
        addProcessor(blurProcessor);
      } else if (backgroundSettings.type === 'image' && typeof backgroundSettings.index === 'number') {
        virtualBackgroundProcessor.backgroundImage = await getImage(backgroundSettings.index);
        addProcessor(virtualBackgroundProcessor);
      } else {
        removeProcessor();
      }
    };
    handleProcessorChange();
    window.localStorage.setItem(SELECTED_BACKGROUND_SETTINGS_KEY, JSON.stringify(backgroundSettings));
  }, [backgroundSettings, videoTrack, room, addProcessor, removeProcessor]);

  return [backgroundSettings, setBackgroundSettings] as const;
}
